/**
 * Prek stream Acuarela v4: HTML scene builders + official NT/math asset URLs.
 * Demo HTML used data-URIs; we resolve ids to /static/visual_*_assets/png/.
 */

import { wait } from "@/utils/prekVisualStream";

const NT_PNG = "/static/visual_nt_assets/png";
const MATH_PNG = "/static/visual_math_assets/png";

/** HTML demo key → official asset id (NT preferred). */
export const HTML_ASSET_ALIASES = {
  clap: "aplaudir",
  arms: "brazos_arriba",
  soap: "jabon",
  brush: "cepillo",
  redball: "pelota",
  blocks: "bloques",
  plate: "plato",
  glass: "vaso",
  pants: "pantalon",
  shirt: "polera",
  doll: "muneca",
  towel: "toalla",
  umbrella: "paraguas",
  rain: "lluvia",
  play: "jugar",
  bone: "hueso",
  guitar: "guitarra",
  soccer: "pelota",
  backMain: "mochila_frente",
  backFront: "mochila_frente",
  backBottom: "mochila_abajo",
  backBack: "mochila_atras",
  cylinder: "cilindro",
  pencil: "lapiz",
  hand: "mano",
};

/** Layout family → component --h (px) from Acuarela v4. */
export const SCENE_HEIGHT = {
  seq: 210,
  matching: 330,
  scene: 330,
  three: 220,
  frequency: 250,
  count: 260,
  equation: 210,
  views: 330,
  shape: 340,
  measure: 360,
  story: 430,
  fallback: 220,
};

const MINI_FRAME =
  '<svg class="mini-frame" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true"><rect pathLength="1" x="1" y="1" width="98" height="98" rx="5"></rect></svg>';

function esc(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function assetUrl(id, { math = false } = {}) {
  if (!id) return null;
  const key = HTML_ASSET_ALIASES[id] || id;
  // hueso lives in math pack; prefer math for known math-only ids
  const mathOnly = new Set([
    "hueso",
    "corazon",
    "campana",
    "lobo",
    "pato",
    "tiburon",
    "tortuga",
    "pinguino",
    "helicoptero",
    "bicicleta",
    "refrigerador",
    "lampara",
    "sombrero",
    "moto",
  ]);
  const useMath = math || mathOnly.has(key);
  return `${useMath ? MATH_PNG : NT_PNG}/${encodeURIComponent(key)}.png`;
}

function img(src, alt, cls = "asset") {
  if (!src) {
    return `<span class="${cls} asset-node asset-missing" aria-label="${esc(
      alt
    )}"></span>`;
  }
  return `<img class="${cls} asset-node" src="${esc(src)}" alt="${esc(alt)}">`;
}

function node(html, cls = "") {
  return `<div class="node ${cls}">${html}</div>`;
}

function box(html, cls = "") {
  return `<div class="box container-node ${cls}">${MINI_FRAME}${html}</div>`;
}

function tipoOf(vm) {
  return (vm && vm.tipo) || "";
}

function layoutForTipo(tipo) {
  const map = {
    patron_corporal_guiado: "seq",
    clasificacion_relacion_lineas: "matching",
    ubicacion_dentro_fuera: "scene",
    ubicacion_linea: "scene",
    comparacion_tres_casillas: "three",
    temporal_seleccion_multiple: "frequency",
    numero: "count",
    escribir: "count",
    composicion_pictorica: "equation",
    operacion_simbolica: "equation",
    vistas_objeto_elegir: "views",
    figuras_observa_responde: "shape",
    figuras_marca_clasifica: "shape",
    medicion_cuenta_opciones: "measure",
    medicion_cuenta_escribe: "measure",
    medicion_compara_unidades: "measure",
    comunicar_proceso_problema: "story",
  };
  return map[tipo] || "fallback";
}

function buildSeq(vm) {
  const seq = (vm.sequence || []).slice(0, 8);
  const items = (seq.length ? seq : ["aplaudir", "brazos_arriba", "aplaudir"]).map(
    (id, i) => box(img(assetUrl(id), id || `movimiento ${i + 1}`))
  );
  while (items.length < 5) {
    items.push(box(img(assetUrl("aplaudir"), "aplaudir")));
  }
  return `<div class="seq">${items.slice(0, 5).join("")}</div>`;
}

function buildMatching(vm) {
  const left = vm.left || [];
  const right = vm.right || [];
  const pairs = [];
  const n = Math.max(left.length, right.length, 4);
  for (let i = 0; i < Math.min(n, 4); i += 1) {
    const L = left[i] || {};
    const R = right[i] || {};
    pairs.push(
      box(
        `${img(assetUrl(L.asset), L.asset || "izq")}<b>${esc(
          L.asset || L.categoria || "…"
        )}</b>`,
        "match"
      )
    );
    pairs.push(
      box(
        `${img(assetUrl(R.asset), R.asset || "der")}<b>${esc(
          R.asset || R.categoria || "…"
        )}</b>`,
        "match"
      )
    );
  }
  return `<div class="matching">${pairs.join("")}</div>`;
}

function buildScene(vm) {
  const tipo = tipoOf(vm);
  const opciones = (vm.opciones || []).slice(0, 4);
  const choices = opciones
    .map((oid) =>
      box(`${img(assetUrl(oid), oid)}<i class="radio"></i>`, "choice")
    )
    .join("");

  if (tipo === "ubicacion_linea") {
    const arriba = vm.arriba || [];
    const mainImgs = [
      { id: arriba[0], cls: "a" },
      { id: arriba[1], cls: "b" },
      { id: vm.abajo, cls: "c" },
    ]
      .filter((x) => x.id)
      .map((a) => img(assetUrl(a.id), a.id, `asset ${a.cls}`))
      .join(" ");
    return `<div class="scene">${box(mainImgs, "scene-main")}${choices}</div>`;
  }

  // ubicacion_dentro_fuera: pink zone + 4 corners (matches NT renderer layout)
  const dentro = vm.dentro || "pelota";
  const fuera = (vm.fuera || []).filter((id) => id && id !== dentro).slice(0, 4);
  const corners = ["tl", "tr", "br", "bl"]
    .map((cls, i) => {
      const id = fuera[i];
      if (!id) return "";
      return img(assetUrl(id), id, `asset corner ${cls}`);
    })
    .join("");
  const pink = `<div class="pink-zone">${img(
    assetUrl(dentro),
    dentro,
    "asset inside"
  )}</div>`;
  return `<div class="scene scene-spatial">${box(
    `${pink}${corners}`,
    "scene-main"
  )}${choices}</div>`;
}

function buildThree(vm) {
  const asset = vm.asset || "bloques";
  const counts = vm.counts || [3, 2, 4];
  const labels = vm.labels || ["A", "B", "C"];
  const cards = [0, 1, 2].map((i) => {
    const n = Number(counts[i]) || 0;
    const objs = Array.from({ length: Math.min(n, 12) }, () =>
      img(assetUrl(asset), asset)
    ).join("");
    return box(
      `<span class="choice-letter">${esc(labels[i] || "ABC"[i])}</span><div class="objects">${objs}</div><i class="radio"></i>`,
      "choice-card"
    );
  });
  return `<div class="three">${cards.join("")}</div>`;
}

function buildFrequency(vm) {
  const items = (vm.items || []).slice(0, 3);
  const cards = (items.length
    ? items
    : [
        { asset: "paraguas", label: "Paraguas" },
        { asset: "lluvia", label: "Lluvia" },
        { asset: "jugar", label: "Jugar" },
      ]
  ).map((it) => {
    const id = typeof it === "string" ? it : it.asset || it.id;
    const label =
      typeof it === "string" ? it : it.label || it.caption || it.asset || id;
    return box(
      `${img(assetUrl(id), label)}<b>${esc(label)}</b><i class="radio"></i>`,
      "choice-card"
    );
  });
  return `<div class="three frequency">${cards.join("")}</div>`;
}

function buildCount(vm) {
  const tipo = tipoOf(vm);
  const objeto = vm.objeto || "hueso";
  const cantidad = Number(vm.cantidad != null ? vm.cantidad : 7) || 7;
  const total = Number(vm.total != null ? vm.total : Math.max(cantidad, 9)) || 9;
  // escribir: empty answer box + exactly `cantidad` objects to count (child writes).
  // numero: big target number + more objects to circle the matching amount.
  if (tipo === "escribir") {
    const items = Array.from({ length: Math.min(Math.max(cantidad, 1), 12) }, () =>
      node(img(assetUrl(objeto, { math: true }), objeto))
    ).join("");
    // Same pink container as numero (borderSweep on .container-node), but empty inside.
    return `<div class="count">${box(
      '<span class="node write-slot" aria-hidden="true">&nbsp;</span>',
      "big-number write-box"
    )}<div class="bones">${items}</div></div>`;
  }
  const show = Math.min(Math.max(total, cantidad), 12);
  const bones = Array.from({ length: show }, () =>
    node(img(assetUrl(objeto, { math: true }), objeto))
  ).join("");
  return `<div class="count">${box(
    String(cantidad),
    "big-number"
  )}<div class="bones">${bones}</div></div>`;
}

function buildEquation(vm) {
  const math = true;
  const objeto = vm.objeto || "guitarra";
  const g1 = Number(vm.group1 != null ? vm.group1 : 3) || 3;
  const g2 = Number(vm.group2 != null ? vm.group2 : 2) || 2;
  const op = vm.operator === "-" ? "−" : "+";
  const mkGroup = (n) =>
    Array.from({ length: Math.min(n, 8) }, () =>
      node(img(assetUrl(objeto, { math }), objeto))
    ).join("");
  const cls1 = g1 === 3 ? "group three-items" : "group";
  const cls2 = g2 === 3 ? "group three-items" : "group";
  return `<div class="equation">${box(mkGroup(g1), cls1)} ${node(
    op,
    "op"
  )} ${box(mkGroup(g2), cls2)} ${node("=", "op")} ${box("", "answer")}</div>`;
}

function buildViews(vm) {
  const ref =
    vm.vista_referencia ||
    (vm.objeto ? `${vm.objeto}_frente` : "mochila_frente");
  const opciones = (vm.opciones || []).slice(0, 3);
  const main = box(
    `${img(assetUrl(ref), "Referencia")}<b>Referencia</b>`,
    "view"
  );
  const opts = (opciones.length
    ? opciones
    : [
        { asset: "mochila_frente", caption: "De frente" },
        { asset: "mochila_abajo", caption: "Desde abajo" },
        { asset: "mochila_atras", caption: "Desde atrás" },
      ]
  ).map((o) => {
    const id = o.asset || o;
    const cap = o.caption || o.letter || id;
    return box(
      `${img(assetUrl(id), cap)}<b>${esc(cap)}</b><i class="radio"></i>`,
      "view"
    );
  });
  return `<div class="views">${main}${opts.join("")}</div>`;
}

function buildShape(vm) {
  const figura = vm.figura || "cilindro";
  const q1 = vm.opciones_nombre || ["cilindro", "esfera", "cubo"];
  const q2 = vm.opciones_lados || ["0 lados", "2 lados", "6 lados"];
  const q3 = vm.opciones_parecido || ["lata", "cono", "círculo"];
  const blocks = [
    ["1. ¿Cómo se llama esta figura?", q1],
    ["2. ¿Cuántos lados tiene?", q2],
    ["3. ¿Qué objeto se parece más?", q3],
  ]
    .map(
      ([title, opts]) =>
        `<div class="question-block"><b class="question-title node">${esc(
          title
        )}</b><div class="qrow">${opts
          .map((x) => box(node(esc(x))))
          .join("")}</div></div>`
    )
    .join("");
  return `<div class="shape">${box(
    img(assetUrl(figura), figura),
    "shape-main"
  )}<div class="questions">${blocks}</div></div>`;
}

function buildMeasure(vm) {
  const tipo = tipoOf(vm);
  const objeto = vm.objeto || "lapiz";
  const unidad = vm.unidad || "mano";
  const count = Math.max(
    1,
    Math.min(
      8,
      Number(vm.unidad_count != null ? vm.unidad_count : 3) || 3
    )
  );
  const letters = "ABCD";
  // Hands/units along the pink guide (same idea as NT PDF renderer).
  const units = Array.from({ length: count }, () =>
    img(assetUrl(unidad), unidad, "asset measure-unit")
  ).join("");
  const board = node(
    `${img(assetUrl(objeto), objeto, "asset pencil")}<div class="measure-units container-node">${units}</div>`,
    "measure-board"
  );
  if (tipo === "medicion_cuenta_escribe") {
    return `<div class="measure">${board}${box(
      `<b class="node">Escribe</b><span class="node write-blank">____</span>`,
      "hand write-slot"
    )}</div>`;
  }
  const opciones = (
    vm.opciones || [count, count + 1, Math.max(1, count - 1), count + 2]
  ).slice(0, 4);
  const choices = opciones
    .map(
      (n, i) =>
        box(
          `<b class="node">${esc(
            `${letters[i]} · ${n}`
          )}</b><i class="radio"></i>`,
          "hand"
        )
    )
    .join("");
  return `<div class="measure">${board}<div class="hands">${choices}</div></div>`;
}

function buildStory(vm) {
  const asset = vm.asset || "pelota";
  const ini = Number(vm.cantidad_inicial != null ? vm.cantidad_inicial : 3) || 3;
  const cam = Number(vm.cantidad_cambio != null ? vm.cantidad_cambio : 2) || 2;
  const pq = vm.opciones_pregunta || [
    "Cuántas hay ahora",
    "Cuántas se quitan",
  ];
  const ac = vm.opciones_accion || ["Juntar", "Separar"];
  const rs = vm.opciones_respuesta || ["4", "5", "2"];
  const steps = [
    ["1. ¿Qué quiero saber?", pq],
    ["2. ¿Qué hago?", ac],
    ["3. Respuesta posible", rs],
  ]
    .map(
      ([title, opts]) =>
        `<div class="step container-node">${MINI_FRAME}<b class="step-title node">${esc(
          title
        )}</b><div class="step-options">${opts
          .map((x) => `<span class="step-option node">${esc(x)}</span>`)
          .join("")}</div></div>`
    )
    .join("");
  const left = Array.from({ length: Math.min(ini, 6) }, () =>
    img(assetUrl(asset), asset)
  ).join("");
  const right = Array.from({ length: Math.min(cam, 6) }, () =>
    img(assetUrl(asset), asset)
  ).join("");
  return `<div class="story">${box(left, "story-panel")} ${node(
    "+",
    "op"
  )} ${box(right, `story-panel${cam === 2 ? " two" : ""}`)}<div class="steps">${steps}</div></div>`;
}

function buildFallbackPng(imageUrl, label) {
  if (!imageUrl) {
    return `<div class="three"><div class="box container-node drawn choice-card"><span class="node show">Sin vista previa</span></div></div>`;
  }
  return `<div class="scene fallback-png">${box(
    img(imageUrl, label || "Vista previa", "asset fallback-full"),
    "scene-main"
  )}</div>`;
}

const BUILDERS = {
  seq: buildSeq,
  matching: buildMatching,
  scene: buildScene,
  three: buildThree,
  frequency: buildFrequency,
  count: buildCount,
  equation: buildEquation,
  views: buildViews,
  shape: buildShape,
  measure: buildMeasure,
  story: buildStory,
};

/**
 * @returns {{ html: string, layout: string, height: number, usedOfficial: boolean }}
 */
export function buildSceneFromConfig(visualMath, { imageUrl = null } = {}) {
  const vm = visualMath || {};
  const tipo = tipoOf(vm);
  const layout = layoutForTipo(tipo);
  const builder = BUILDERS[layout];
  if (!builder || !tipo) {
    if (imageUrl) {
      console.warn("[prekStreamScenes] fallback PNG for missing config", tipo);
      return {
        html: buildFallbackPng(imageUrl, tipo),
        layout: "fallback",
        height: SCENE_HEIGHT.fallback,
        usedOfficial: false,
      };
    }
    return {
      html: buildFallbackPng(null),
      layout: "fallback",
      height: SCENE_HEIGHT.fallback,
      usedOfficial: false,
    };
  }
  try {
    return {
      html: builder(vm),
      layout,
      height: SCENE_HEIGHT[layout] || SCENE_HEIGHT.fallback,
      usedOfficial: true,
    };
  } catch (err) {
    console.warn("[prekStreamScenes] builder failed", tipo, err);
    return {
      html: buildFallbackPng(imageUrl, tipo),
      layout: "fallback",
      height: SCENE_HEIGHT.fallback,
      usedOfficial: false,
    };
  }
}

export async function typeTextEl(el, text) {
  if (!el) return;
  el.textContent = "";
  const cursor = document.createElement("i");
  cursor.className = "cursor";
  el.appendChild(cursor);
  const full = text || "";
  for (let i = 0; i < full.length; i += 5) {
    el.insertBefore(document.createTextNode(full.slice(i, i + 5)), cursor);
    await wait(8);
  }
  cursor.remove();
}

/**
 * Acuarela v4 animateCard stages on a mounted card element.
 */
export async function animateAcuarelaCard(cardEl, statement) {
  if (!cardEl) return;
  cardEl.classList.add("active", "draw-card");
  await wait(280);
  const prompt = cardEl.querySelector(".prompt");
  await typeTextEl(prompt, statement);
  const component = cardEl.querySelector(".component");
  if (component) component.classList.add("draw-frame");
  await wait(70);
  const containers = [...cardEl.querySelectorAll(".container-node")];
  const drawPause = Math.max(
    105,
    Math.min(145, 1450 / Math.max(1, containers.length))
  );
  for (const container of containers) {
    container.classList.add("drawn");
    await wait(drawPause);
  }
  const measure = cardEl.querySelector(".measure-board");
  if (measure) {
    measure.classList.add("line-ready");
    await wait(260);
  }
  const nodes = [...cardEl.querySelectorAll(".node")];
  for (const n of nodes) {
    n.classList.add("show");
    await wait(Math.max(18, Math.min(45, 360 / Math.max(1, nodes.length))));
  }
  const assets = [...cardEl.querySelectorAll(".asset-node")];
  for (const asset of assets) {
    asset.classList.add("show");
    await wait(Math.max(22, Math.min(52, 460 / Math.max(1, assets.length))));
  }
  await wait(90);
  cardEl.classList.remove("active");
  cardEl.classList.add("complete");
  await wait(620);
}

export async function smoothScrollFeed(feedEl, target, duration = 520) {
  if (!feedEl) return;
  const start = feedEl.scrollTop;
  const delta = target - start;
  const t0 = performance.now();
  const ease = (t) => 1 - Math.pow(1 - t, 4);
  await new Promise((resolve) => {
    const step = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      feedEl.scrollTop = start + delta * ease(p);
      if (p < 1) requestAnimationFrame(step);
      else resolve();
    };
    requestAnimationFrame(step);
  });
}
